import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PerformancePanel({ from, to, timeframe = "5m" }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/stats/${from}/${to}`, { params: { timeframe } })
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [from, to, timeframe]);

  if (loading) {
    return (
      <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
        <div className="animate-pulse h-40 bg-zinc-900" />
      </Card>
    );
  }
  if (!data || data.trades === 0) {
    return (
      <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6" data-testid="performance-empty">
        <div className="flex items-center gap-2 text-emerald-400 text-xs mb-3">
          <BarChart3 className="h-3.5 w-3.5" />
          <span>{t("performance")}</span>
        </div>
        <p className="text-sm text-zinc-500">{t("no_history")}</p>
      </Card>
    );
  }

  const wr = data.win_rate;
  const wrColor = wr >= 60 ? "#10B981" : wr >= 45 ? "#F59E0B" : "#EF4444";
  const winStreak = data.recent.filter(r => r.outcome === "win").length;

  return (
    <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6" data-testid="performance-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-emerald-400 text-xs">
          <BarChart3 className="h-3.5 w-3.5" />
          <span className="font-bold">{t("performance")}</span>
          <span className="text-zinc-600 mx-1">·</span>
          <span className="text-zinc-500">{t("perf_desc")}</span>
        </div>
        <div className="flex items-center gap-4">
          <Stat label={t("winrate")} value={`${data.win_rate}%`} color={wrColor} />
          <Stat label={t("trades")} value={data.trades} />
          <Stat label="Avg P&L" value={`${data.avg_pnl >= 0 ? "+" : ""}${data.avg_pnl}%`} color={data.avg_pnl >= 0 ? "#10B981" : "#EF4444"} />
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[10px] text-zinc-500 mb-2">{t("win_rate_evolution")}</div>
        <div className="h-40" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.points} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
              <XAxis dataKey="index" stroke="#71717a" fontSize={10} tick={{ fontFamily: 'JetBrains Mono' }} />
              <YAxis domain={[0, 100]} stroke="#71717a" fontSize={10} orientation="right" tick={{ fontFamily: 'JetBrains Mono' }} />
              <ReferenceLine y={50} stroke="#3f3f46" strokeDasharray="4 4" />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono" }}
                labelStyle={{ color: "#a1a1aa" }}
                formatter={(v) => [`${v}%`, t("winrate")]}
              />
              <Line type="monotone" dataKey="win_rate" stroke={wrColor} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="text-[10px] text-zinc-500 mb-2">{t("recent_trades")}</div>
        <div className="flex flex-wrap gap-1.5" dir="ltr">
          {data.recent.map((r, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold num",
                r.outcome === "win" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                                    : "bg-red-500/15 text-red-400 border border-red-500/30"
              )}
              title={`${r.signal} · ${r.pnl_pct}%`}
            >
              {r.outcome === "win" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              <span>{r.pnl_pct > 0 ? "+" : ""}{r.pnl_pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="text-[9px] text-zinc-600 uppercase tracking-wider">{label}</div>
      <div className="num text-base font-bold" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}
